import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Clock } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { getRouteForNavigation } from "@/utils/routeAccess";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { getServiceName } from "@/utils/dataLookups";

export default function UpcomingAppointments({
  appointments,
  isLoading,
  viewAllUrl = "Calendar",
  title = "Upcoming Appointments",
  isPatientView = false,
}) {
  // Get all services to map service IDs to names
  const { data: allServices = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => apiClient.get("/api/services"),
  });

  const upcomingBookings =
    appointments
      ?.filter(
        (booking) =>
          new Date(booking.date) >= new Date() && booking.status === "scheduled"
      )
      .slice(0, 3) || [];

  const nextAppointment = upcomingBookings[0];

  return (
    <>
      {/* Next Appointment Highlight */}
      {nextAppointment && (
        <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200/60 shadow-xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">
                    Next Appointment
                  </h3>
                  <p className="text-slate-600">
                    {format(new Date(nextAppointment.date), "EEEE, MMMM d")} at{" "}
                    {nextAppointment.startTime}
                  </p>
                  {nextAppointment.serviceId && (
                    <p className="text-sm text-slate-500">
                      Service: {getServiceName(nextAppointment.serviceId, allServices)}
                    </p>
                  )}
                </div>
              </div>
              <Link to={getRouteForNavigation(viewAllUrl)}>
                <Button variant="outline" size="sm">
                  View All
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appointments List */}
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-900">
              <Calendar className="w-5 h-5 text-green-600" />
              {title}
            </div>
            <Link to={getRouteForNavigation(viewAllUrl)}>
              <Button variant="ghost" size="sm">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-slate-200 rounded-lg"></div>
                  </div>
                ))}
            </div>
          ) : upcomingBookings.length > 0 ? (
            <div className="space-y-3">
              {upcomingBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="p-4 bg-slate-50/60 rounded-lg border border-slate-200/60 hover:bg-slate-100/60 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-slate-900">
                      {format(new Date(booking.date), "EEEE, MMM d")}
                    </h4>
                    <span className="text-sm font-medium text-slate-700">
                      {booking.startTime}
                    </span>
                  </div>
                  {booking.serviceId && (
                    <p className="text-sm text-slate-600">
                      Service: {getServiceName(booking.serviceId, allServices)}
                    </p>
                  )}
                  {booking.doctorId && !isPatientView && (
                    <p className="text-sm text-slate-500">
                      Doctor: {booking.doctorId}
                    </p>
                  )}
                  {booking.patientId && !isPatientView && (
                    <p className="text-sm text-slate-500">
                      Patient: {booking.patientId}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No upcoming appointments</p>
              <Link to={getRouteForNavigation(viewAllUrl)}>
                <Button variant="outline" size="sm" className="mt-2">
                  {isPatientView
                    ? "Book Your First Appointment"
                    : "Schedule Appointment"}
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
